<template>
  <main-template class="meu-banco-page">
    <v-row align="center" class="mb-3 fade-up d1">
      <v-col cols="12" sm="8">
        <div class="d-flex align-center">
          <v-btn class="back-btn mr-3" icon @click="$router.go(-1)">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h1 class="page-title">Meu Banco de Horas</h1>
            <div class="page-subtitle">
              Acompanhe seu desempenho diário, saldo acumulado e a evolução das suas jornadas.
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card outlined rounded="xl" class="mb-6 hero-card overflow-hidden elevation-1 fade-up d2">
      <div class="hero-accent-bar"></div>
      <div class="hero-orb"></div>
      <v-card-text class="pa-6">
        <v-row align="center" justify="space-between">
          <v-col cols="12" md="7">
            <div class="d-flex align-center mb-2">
              <div class="hero-badge mr-3">
                <v-icon color="white">mdi-shimmer</v-icon>
              </div>
              <div>
                <span class="hero-title">Resumo do Seu Desempenho</span>
                <v-chip
                  x-small
                  :color="hojeResumo.statusColor"
                  class="ml-2 font-weight-bold white--text text-capitalize"
                >
                  {{ hojeResumo.statusTexto }}
                </v-chip>
              </div>
            </div>
            <p class="hero-message mb-0">{{ mensagemInteligente }}</p>
          </v-col>

          <v-col cols="12" md="5" class="mt-4 mt-md-0">
            <div class="today-panel">
              <div class="today-label">Desempenho de Hoje</div>
              <div class="today-stats">
                <div class="today-stat">
                  <div class="today-stat-label">Trabalhado</div>
                  <div class="today-stat-value today-stat-value--purple">
                    {{ hojeResumo.trabalhadoFmt }}
                  </div>
                </div>
                <div class="today-divider"></div>
                <div class="today-stat">
                  <div class="today-stat-label">Jornada</div>
                  <div class="today-stat-value">{{ hojeResumo.previstoFmt }}</div>
                </div>
                <div class="today-divider"></div>
                <div class="today-stat">
                  <div class="today-stat-label">Diferença</div>
                  <div
                    :class="[
                      'today-stat-value',
                      hojeResumo.diferencaMinutos >= 0
                        ? 'today-stat-value--green'
                        : 'today-stat-value--amber',
                    ]"
                  >
                    {{ hojeResumo.diferencaFmt }}
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row class="mb-6">
      <v-col
        v-for="m in kpiCards"
        :key="m.key"
        cols="12"
        sm="6"
        md="3"
        class="metric-col fade-up d3"
      >
        <div class="metric-card">
          <div class="metric-card-orb"></div>
          <div class="d-flex justify-space-between align-start">
            <div class="metric-info">
              <div class="metric-label">{{ m.label }}</div>
              <div class="metric-value" :class="m.tone">{{ m.value }}</div>
              <div class="metric-sub">{{ m.sub }}</div>
              <v-progress-linear
                v-if="m.progress !== null"
                :value="m.progress"
                :color="m.progressColor"
                height="6"
                rounded
                class="mt-2"
                style="max-width: 120px"
              ></v-progress-linear>
            </div>
            <div class="metric-icon" :class="m.tone">
              <v-icon small color="white">{{ m.icon }}</v-icon>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row class="mb-6">
      <v-col cols="12" md="7">
        <v-card class="panel-card pa-5 fade-up d4">
          <div class="d-flex justify-space-between align-center mb-4">
            <div>
              <div class="card-title">Evolução da Jornada</div>
              <div class="card-subtitle">
                Comparativo de horas realizadas vs previstas no período
              </div>
            </div>
            <div class="card-title-badge tone-purple">
              <v-icon small color="white">mdi-chart-areaspline</v-icon>
            </div>
          </div>

          <div v-if="chartData.series[0].data.length > 0">
            <apexchart
              type="area"
              height="260"
              :options="chartData.options"
              :series="chartData.series"
            />
          </div>
          <div v-else class="pa-8 text-center grey--text">
            <v-icon size="40" color="grey lighten-1">mdi-chart-box-outline</v-icon>
            <div class="mt-2 text-caption">Histórico em processamento para o gráfico.</div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card class="panel-card pa-5 d-flex flex-column justify-space-between fade-up d5">
          <div>
            <div class="d-flex justify-space-between align-center mb-4">
              <div>
                <div class="card-title">Composição do Saldo</div>
                <div class="card-subtitle">Balanço atualizado do seu banco de horas</div>
              </div>
              <div class="card-title-badge tone-blue">
                <v-icon small color="white">mdi-scale-balance</v-icon>
              </div>
            </div>

            <div
              :class="[
                'saldo-box mb-4',
                isSaldoNegativo ? 'saldo-box--negativo' : 'saldo-box--positivo',
              ]"
            >
              <div class="saldo-label">Saldo Atual do Período</div>
              <div
                class="saldo-value"
                :class="isSaldoNegativo ? 'saldo-text-amber' : 'saldo-text-green'"
              >
                {{ saldoData.saldo_formatado || "00:00" }}
              </div>
              <div class="saldo-hint">
                {{
                  isSaldoNegativo ? "Compensação dinâmica em andamento" : "Saldo positivo acumulado"
                }}
              </div>
            </div>

            <div>
              <div class="balance-row">
                <div class="d-flex align-center">
                  <div class="balance-icon tone-green">
                    <v-icon x-small color="white">mdi-plus</v-icon>
                  </div>
                  <span class="balance-row-label">Créditos Aprovados</span>
                </div>
                <span class="balance-row-value text-green">
                  +{{ saldoData.horas_positivas_fmt || "00:00" }}
                </span>
              </div>

              <div class="balance-row">
                <div class="d-flex align-center">
                  <div class="balance-icon tone-amber">
                    <v-icon x-small color="white">mdi-clock-outline</v-icon>
                  </div>
                  <span class="balance-row-label">Horas em Análise</span>
                </div>
                <span class="balance-row-value text-amber">
                  {{ saldoData.minutos_pendentes_fmt || "00:00" }}
                </span>
              </div>

              <div class="balance-row">
                <div class="d-flex align-center">
                  <div class="balance-icon tone-slate">
                    <v-icon x-small color="white">mdi-minus</v-icon>
                  </div>
                  <span class="balance-row-label">Ajustes de Jornada</span>
                </div>
                <span class="balance-row-value text-slate">
                  -{{ saldoData.horas_negativas_fmt || "00:00" }}
                </span>
              </div>
            </div>
          </div>

          <div class="mt-4 pt-3 border-top text-caption grey--text text--darken-1 text-center">
            <v-icon x-small color="grey darken-1">mdi-information-outline</v-icon>
            Os saldos são calculados com base nas regras vigentes de tolerância e escala.
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card
      class="panel-card elevation-1 white history-card fade-up d6 mb-6"
      style="border-radius: 16px; overflow: hidden"
    >
      <div class="d-flex align-center px-6 pt-6 pb-2">
        <div class="card-title-badge tone-purple mr-3">
          <v-icon small color="white">mdi-history</v-icon>
        </div>
        <div>
          <div class="card-title">Histórico de Movimentações</div>
          <div class="card-subtitle">Lançamentos e ajustes do seu banco de horas</div>
        </div>
      </div>

      <v-card-text class="px-6 pb-6">
        <v-row class="mb-4">
          <v-col cols="12" sm="3" md="3">
            <e-label>Tipo de Registro</e-label>
            <v-select
              v-model="filtros.tipo"
              :items="tipoOptions"
              item-text="text"
              item-value="value"
              dense
              solo
              clearable
              hide-details
              @change="carregarHistorico(1)"
            />
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <e-label>Status da Aprovação</e-label>
            <v-select
              v-model="filtros.status"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              dense
              solo
              clearable
              hide-details
              @change="carregarHistorico(1)"
            />
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <e-label>Data Início</e-label>
            <e-date-customize v-model="filtros.data_inicio" @change="carregarHistorico(1)" />
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <e-label>Data Fim</e-label>
            <e-date-customize v-model="filtros.data_fim" @change="carregarHistorico(1)" />
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="historico.data || []"
          :loading="loading"
          hide-default-footer
          class="elevation-0 custom-table"
        >
          <template v-slot:item.data="{ item }">
            <span class="font-weight-bold grey--text text--darken-3">
              {{ formatarData(item.data) }}
            </span>
          </template>

          <template v-slot:item.minutos_previstos="{ item }">
            {{ formatarMinutos(item.minutos_previstos) }}
          </template>

          <template v-slot:item.minutos_realizados="{ item }">
            <span class="font-weight-medium">
              {{ formatarMinutos(item.minutos_realizados) }}
            </span>
          </template>

          <template v-slot:item.minutos_extras="{ item }">
            <v-chip
              v-if="item.tipo === 'debito'"
              small
              color="blue-grey lighten-5"
              text-color="blue-grey darken-3"
              class="font-weight-bold"
            >
              -{{ formatarMinutos(item.minutos_debito) }}
            </v-chip>
            <v-chip
              v-else
              small
              color="green lighten-5"
              text-color="green darken-3"
              class="font-weight-bold"
            >
              +{{ formatarMinutos(item.minutos_extras) }}
            </v-chip>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              v-if="item.tipo === 'debito'"
              small
              color="blue-grey lighten-4"
              text-color="blue-grey darken-3"
              class="font-weight-bold"
            >
              Ajuste de Jornada
            </v-chip>
            <v-chip
              v-else
              small
              :color="getStatusColor(item.status)"
              class="font-weight-bold white--text"
            >
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.aprovador="{ item }">
            <span v-if="item.aprovador">{{ item.aprovador.name }}</span>
            <span v-else-if="item.tipo === 'debito'" class="grey--text">Registro do Sistema</span>
            <span
              v-else-if="item.status === 'pendente'"
              class="amber--text text--darken-3 font-italic"
              >Em análise pelo gestor</span
            >
            <span v-else class="grey--text">-</span>
          </template>

          <template v-slot:item.observacoes="{ item }">
            <span class="text-caption grey--text text--darken-2">
              {{
                item.motivo_reprovacao
                  ? "Motivo: " + item.motivo_reprovacao
                  : item.observacoes || "-"
              }}
            </span>
          </template>

          <template v-slot:no-data>
            <div class="pa-8 text-center grey--text">
              <v-icon size="48" color="grey lighten-1">mdi-clock-off-outline</v-icon>
              <div class="mt-2 text-body-2 font-weight-medium">
                Nenhuma movimentação de banco de horas encontrada.
              </div>
            </div>
          </template>
        </v-data-table>

        <v-pagination
          v-if="historico.last_page > 1"
          v-model="historico.current_page"
          :length="historico.last_page"
          @input="carregarHistorico"
          class="mt-4"
        />
      </v-card-text>
    </v-card>
  </main-template>
</template>

<script>
export default {
  name: "MeuBancoHorasPage",
  data() {
    return {
      loading: false,
      saldoData: {
        saldo_minutos: 0,
        horas_negativas_minutos: 0,
        saldo_formatado: "00:00",
        horas_positivas_fmt: "00:00",
        horas_negativas_fmt: "00:00",
        minutos_pendentes_fmt: "00:00",
        em_ferias: false,
      },
      historico: {
        data: [],
        current_page: 1,
        last_page: 1,
      },
      filtros: {
        tipo: null,
        status: null,
        data_inicio: null,
        data_fim: null,
      },
      tipoOptions: [
        { text: "Todos os Tipos", value: null },
        { text: "Créditos (Horas Extras)", value: "credito" },
        { text: "Ajustes de Jornada", value: "debito" },
      ],
      statusOptions: [
        { text: "Pendente de Aprovação", value: "pendente" },
        { text: "Aprovado", value: "aprovado" },
        { text: "Reprovado", value: "reprovado" },
      ],
      headers: [
        { text: "Data", value: "data", sortable: false },
        { text: "Jornada Prevista", value: "minutos_previstos", sortable: false },
        { text: "Horas Realizadas", value: "minutos_realizados", sortable: false },
        { text: "Movimentação", value: "minutos_extras", sortable: false },
        { text: "Status / Registro", value: "status", sortable: false },
        { text: "Responsável", value: "aprovador", sortable: false },
        { text: "Observações", value: "observacoes", sortable: false },
      ],
    };
  },
  computed: {
    isSaldoNegativo() {
      return (this.saldoData?.saldo_minutos || 0) < 0;
    },
    hojeResumo() {
      const hoje = new Date();
      const hojeStr = [
        hoje.getFullYear(),
        String(hoje.getMonth() + 1).padStart(2, "0"),
        String(hoje.getDate()).padStart(2, "0"),
      ].join("-");
      const list = this.historico.data || [];
      const itemHoje = list.find((item) => {
        const d = item.data ? item.data.split("T")[0] : "";
        return d === hojeStr;
      });

      if (!itemHoje) {
        if (this.saldoData.em_ferias) {
          return {
            trabalhadoFmt: "00:00",
            previstoFmt: "00:00",
            diferencaFmt: "00:00",
            diferencaMinutos: 0,
            statusTexto: "Férias",
            statusColor: "blue-grey darken-1",
            trabalhou: false,
          };
        }
        return {
          trabalhadoFmt: "00:00",
          previstoFmt: "00:00",
          diferencaFmt: "00:00",
          diferencaMinutos: 0,
          statusTexto: "Folga / Não Previsto",
          statusColor: "blue-grey darken-1",
          trabalhou: false,
        };
      }

      const previstos = itemHoje.minutos_previstos || 0;
      const realizados = itemHoje.minutos_realizados || 0;
      const diferenca =
        itemHoje.tipo === "credito"
          ? itemHoje.minutos_extras || realizados - previstos
          : -(itemHoje.minutos_debito || previstos - realizados);

      let statusTexto = "Jornada Completa";
      let statusColor = "teal darken-1";

      if (diferenca > 0) {
        statusTexto = "Horas Extras Realizadas";
        statusColor = "green darken-1";
      } else if (diferenca < 0) {
        statusTexto = "Jornada em Ajuste";
        statusColor = "amber darken-2";
      } else if (previstos === 0) {
        statusTexto = "Folga / Não Previsto";
        statusColor = "blue-grey darken-1";
      }

      return {
        trabalhadoFmt: this.formatarMinutos(realizados),
        previstoFmt: this.formatarMinutos(previstos),
        diferencaFmt: this.formatarMinutosSigned(diferenca),
        diferencaMinutos: diferenca,
        statusTexto,
        statusColor,
        trabalhou: realizados > 0,
      };
    },
    mensagemInteligente() {
      const hoje = this.hojeResumo;
      if (this.saldoData.em_ferias) {
        return `Você está de férias. Seu retorno será acompanhado normalmente pelo banco de horas.`;
      }
      if (hoje.diferencaMinutos > 0) {
        return `Parabéns pelo empenho hoje! Você acumulou ${hoje.diferencaFmt} adicionais na sua jornada diária.`;
      }
      if (hoje.trabalhou) {
        return `Sua jornada de hoje (${hoje.trabalhadoFmt}) foi registrada e concluída normalmente com sucesso.`;
      }
      if (!this.isSaldoNegativo) {
        return "Seu banco de horas possui saldo positivo saudável disponível para acompanhamento.";
      }
      return "Seu banco de horas é dinâmico e compensado continuamente à medida que você realiza suas próximas jornadas de trabalho.";
    },
    kpiMetrics() {
      const semana = this.saldoData.semana || {};
      const totalRealizadoMin = semana.realizado_semana_minutos || 0;
      const totalPrevistoMin = semana.previsto_semana_minutos || 0;

      const taxaCumprimento =
        totalPrevistoMin > 0
          ? Math.min(100, Math.round((totalRealizadoMin / totalPrevistoMin) * 100))
          : 100;

      const formatarPeriodo = () => {
        if (!semana.inicio_semana || !semana.fim_semana) return "";
        return `${this.formatarData(semana.inicio_semana)} a ${this.formatarData(
          semana.fim_semana
        )}`;
      };

      return {
        totalRealizadoFmt: this.formatarMinutos(totalRealizadoMin),
        totalPrevistoFmt: this.formatarMinutos(totalPrevistoMin),
        taxaCumprimento,
        periodoSemana: formatarPeriodo(),
      };
    },
    kpiCards() {
      return [
        {
          key: "realizadas",
          label: "Horas Realizadas",
          value: this.kpiMetrics.totalRealizadoFmt,
          sub: `de ${this.kpiMetrics.totalPrevistoFmt} previstas na semana`,
          icon: "mdi-clock-check-outline",
          tone: "metric-tone-purple",
          progress: null,
          progressColor: null,
        },
        {
          key: "cumprimento",
          label: "Cumprimento da Jornada",
          value: `${this.kpiMetrics.taxaCumprimento}%`,
          sub: "Meta da semana",
          icon: "mdi-chart-line",
          tone: "metric-tone-teal",
          progress: this.kpiMetrics.taxaCumprimento,
          progressColor: "#14b8a6",
        },
        {
          key: "extras",
          label: "Horas Extras Aprovadas",
          value: `+${this.saldoData.horas_positivas_fmt || "00:00"}`,
          sub: "Créditos no banco de horas",
          icon: "mdi-plus-circle-outline",
          tone: "metric-tone-green",
          progress: null,
          progressColor: null,
        },
        {
          key: "analise",
          label: "Horas em Análise",
          value: this.saldoData.minutos_pendentes_fmt || "00:00",
          sub: "Aguardando aprovação",
          icon: "mdi-clock-alert-outline",
          tone: "metric-tone-amber",
          progress: null,
          progressColor: null,
        },
      ];
    },
    chartData() {
      // Lê o tema para recalcular as cores do gráfico ao alternar claro/escuro.
      const escuro = this.$vuetify.theme.dark;
      const corEixo = escuro ? "#9a93ad" : "#64748b";
      const list = [...(this.historico.data || [])].reverse();
      const categories = list.map((item) => this.formatarDataCurta(item.data));
      const seriesRealizados = list.map(
        (item) => Math.round(((item.minutos_realizados || 0) / 60) * 100) / 100
      );
      const seriesPrevistos = list.map(
        (item) => Math.round(((item.minutos_previstos || 0) / 60) * 100) / 100
      );

      return {
        series: [
          {
            name: "Horas Realizadas (h)",
            data: seriesRealizados,
          },
          {
            name: "Jornada Prevista (h)",
            data: seriesPrevistos,
          },
        ],
        options: {
          theme: { mode: escuro ? "dark" : "light" },
          chart: {
            type: "area",
            height: 260,
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: "Roboto, sans-serif",
            foreColor: corEixo,
            background: "transparent",
          },
          colors: [escuro ? "#a78bfa" : "#4f008f", "#10b981"],
          fill: {
            type: "gradient",
            gradient: {
              shade: escuro ? "dark" : "light",
              shadeIntensity: 1,
              opacityFrom: 0.35,
              opacityTo: 0.05,
              stops: [0, 90, 100],
            },
          },
          dataLabels: { enabled: false },
          stroke: { curve: "smooth", width: 2.5 },
          xaxis: {
            categories,
            labels: { style: { colors: corEixo, fontSize: "11px" } },
          },
          yaxis: {
            labels: {
              formatter: (val) => `${val}h`,
              style: { colors: corEixo, fontSize: "11px" },
            },
          },
          tooltip: {
            theme: escuro ? "dark" : "light",
            y: {
              formatter: (val) => `${val} horas`,
            },
          },
          legend: {
            position: "top",
            horizontalAlign: "right",
          },
          grid: {
            borderColor: escuro ? "#2a2638" : "#f1f5f9",
            strokeDashArray: 4,
            row: { colors: ["transparent"] },
          },
        },
      };
    },
  },
  async created() {
    this.$loaderService.open("Carregando dados...");
    try {
      await this.carregarDados();
    } finally {
      this.$loaderService.close();
    }
  },
  methods: {
    async carregarDados() {
      this.loading = true;
      try {
        const saldoResp = await this.$services.bancoHorasService.getMeuSaldo();
        this.saldoData = saldoResp || {};
        await this.carregarHistorico(1);
      } catch (e) {
        this.$toast.danger(e?.response?.data?.message || "Erro ao carregar banco de horas.");
      } finally {
        this.loading = false;
      }
    },
    async carregarHistorico(page = 1) {
      this.loading = true;
      try {
        const params = {
          page,
          ...this.filtros,
        };
        const resp = await this.$services.bancoHorasService.getMeuHistorico(params);
        this.historico = resp || { data: [], current_page: 1, last_page: 1 };
      } catch (e) {
        /* eslint-disable-next-line no-console */
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    formatarMinutos(minutos) {
      if (minutos === null || minutos === undefined) return "00:00";
      const sinal = minutos < 0 ? "-" : "";
      const abs = Math.abs(minutos);
      const h = Math.floor(abs / 60);
      const m = abs % 60;
      return `${sinal}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    },
    formatarMinutosSigned(minutos) {
      if (minutos === null || minutos === undefined) return "00:00";
      let sinal = "";
      if (minutos > 0) {
        sinal = "+";
      } else if (minutos < 0) {
        sinal = "-";
      }
      const abs = Math.abs(minutos);
      const h = Math.floor(abs / 60);
      const m = abs % 60;
      return `${sinal}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    },
    formatarData(dataStr) {
      if (!dataStr) return "-";
      const partes = dataStr.split("T")[0].split("-");
      if (partes.length === 3) {
        return `${partes[2]}/${partes[1]}/${partes[0]}`;
      }
      return dataStr;
    },
    formatarDataCurta(dataStr) {
      if (!dataStr) return "-";
      const partes = dataStr.split("T")[0].split("-");
      if (partes.length === 3) {
        return `${partes[2]}/${partes[1]}`;
      }
      return dataStr;
    },
    getStatusColor(status) {
      if (status === "aprovado") return "green darken-1";
      if (status === "reprovado") return "red lighten-1";
      return "amber darken-2";
    },
    getStatusText(status) {
      if (status === "aprovado") return "Aprovado";
      if (status === "reprovado") return "Não Aprovado";
      return "Em Análise";
    },
  },
};
</script>

<style scoped>
/* Tons locais da página: claro por padrão, sobrescritos no modo escuro. */
.meu-banco-page {
  --tint-purple: #faf8ff;
  --tint-green: #f0fdf4;
  --tint-green-2: #f7fef9;
  --tint-green-border: #bbf7d0;
  --tint-amber: #fffbeb;
  --tint-amber-2: #fffdf5;
  --tint-amber-border: #fde68a;
  --fg-green: #059669;
  --fg-green-deep: #15803d;
  --fg-amber: #d97706;
  --fg-amber-deep: #b45309;
}

.theme--dark .meu-banco-page {
  --tint-purple: #211c2c;
  --tint-green: rgba(34, 197, 94, 0.14);
  --tint-green-2: rgba(34, 197, 94, 0.06);
  --tint-green-border: rgba(74, 222, 128, 0.3);
  --tint-amber: rgba(245, 158, 11, 0.14);
  --tint-amber-2: rgba(245, 158, 11, 0.06);
  --tint-amber-border: rgba(251, 191, 36, 0.3);
  --fg-green: #34d399;
  --fg-green-deep: #4ade80;
  --fg-amber: #fbbf24;
  --fg-amber-deep: #fbbf24;
}

/* ===== CABEÇALHO ===== */
.page-title {
  font-size: 21px;
  font-weight: 800;
  color: var(--text);
  line-height: 1.2;
  margin: 0;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-muted);
  font-weight: 500;
  margin-top: 2px;
}

.back-btn {
  background: var(--brand-soft-2) !important;
  color: var(--brand) !important;
  border-radius: 11px;
  box-shadow: 0 4px 10px rgba(79, 0, 143, 0.12);
  transition: background-color 0.18s ease, transform 0.18s ease !important;
}

.back-btn:hover {
  background: var(--brand-border) !important;
  transform: scale(1.05);
}

/* ===== HERO ===== */
.hero-card {
  position: relative;
  background: linear-gradient(135deg, var(--surface) 0%, var(--tint-purple) 100%);
  border: 1px solid var(--brand-border) !important;
}

.hero-orb {
  position: absolute;
  top: -70px;
  right: -30px;
  width: 220px;
  height: 190px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(124, 58, 237, 0.1), transparent 70%);
  pointer-events: none;
}

.hero-accent-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #4f008f 0%, #7e22ce 100%);
}

.hero-badge {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #4f008f, #7c3aed);
  box-shadow: 0 8px 18px rgba(79, 0, 143, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hero-title {
  font-size: 15px;
  font-weight: 800;
  color: var(--text-2);
}

.hero-message {
  font-size: 13.5px;
  color: var(--text-muted);
  margin-top: 6px;
  padding-left: 4px;
}

/* ===== PAINEL DE HOJE ===== */
.today-panel {
  background: linear-gradient(135deg, var(--brand-soft), var(--tint-purple));
  border: 1px solid var(--brand-border);
  border-radius: 14px;
  padding: 16px;
  position: relative;
  overflow: hidden;
}

.today-label {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--brand-3);
  margin-bottom: 12px;
}

.today-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: center;
}

.today-stat {
  flex: 1;
}

.today-stat-label {
  font-size: 11px;
  color: var(--text-faint);
  font-weight: 600;
}

.today-stat-value {
  font-size: 15px;
  font-weight: 800;
  color: var(--text-2);
  margin-top: 2px;
}

.today-stat-value--purple {
  background: linear-gradient(90deg, var(--brand), var(--brand-3));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.today-stat-value--green {
  color: var(--fg-green);
}

.today-stat-value--amber {
  color: var(--fg-amber);
}

.today-divider {
  width: 1px;
  height: 26px;
  background: var(--brand-border);
}

/* ===== CARDS MÉTRICOS ===== */
.metric-card {
  position: relative;
  height: 100%;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 18px;
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.metric-card:hover {
  transform: translateY(-3px);
  border-color: rgba(124, 58, 237, 0.35);
  box-shadow: 0 14px 30px rgba(79, 0, 143, 0.12);
}

.metric-card-orb {
  position: absolute;
  top: -40px;
  right: -34px;
  width: 120px;
  height: 110px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(124, 58, 237, 0.08), transparent 70%);
  pointer-events: none;
}

.metric-label {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--text-faint);
}

.metric-value {
  font-size: 26px;
  font-weight: 800;
  line-height: 1.15;
  margin-top: 6px;
}

.metric-value.metric-tone-purple {
  background: linear-gradient(90deg, var(--brand), var(--brand-3));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.metric-value.metric-tone-teal {
  background: linear-gradient(90deg, #0d9488, #2dd4bf);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.metric-value.metric-tone-green {
  background: linear-gradient(90deg, var(--fg-green-deep), #22c55e);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.metric-value.metric-tone-amber {
  background: linear-gradient(90deg, var(--fg-amber-deep), #f59e0b);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.metric-sub {
  font-size: 12px;
  color: var(--text-faint);
  margin-top: 4px;
}

.metric-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);
}

.metric-icon.metric-tone-purple {
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
}

.metric-icon.metric-tone-teal {
  background: linear-gradient(135deg, #0d9488, #2dd4bf);
}

.metric-icon.metric-tone-green {
  background: linear-gradient(135deg, #15803d, #22c55e);
}

.metric-icon.metric-tone-amber {
  background: linear-gradient(135deg, #d97706, #fbbf24);
}

/* ===== CARDS DE PAINEL (GRÁFICO / SALDO) ===== */
.panel-card {
  position: relative;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  height: 100%;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.card-title {
  font-size: 15px;
  font-weight: 800;
  color: var(--text);
}

.card-subtitle {
  font-size: 12px;
  color: var(--text-faint);
  margin-top: 2px;
}

.card-title-badge {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 5px 12px rgba(15, 23, 42, 0.12);
}

.card-title-badge.tone-purple {
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
}

.card-title-badge.tone-blue {
  background: linear-gradient(135deg, #0284c7, #38bdf8);
}

/* ===== BOX DO SALDO ===== */
.saldo-box {
  border-radius: 14px;
  padding: 16px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.saldo-box--positivo {
  background: linear-gradient(135deg, var(--tint-green), var(--tint-green-2)) !important;
  border: 1px solid var(--tint-green-border);
}

.saldo-box--negativo {
  background: linear-gradient(135deg, var(--tint-amber), var(--tint-amber-2)) !important;
  border: 1px solid var(--tint-amber-border);
}

.saldo-label {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--text-faint);
}

.saldo-value {
  font-size: 30px;
  font-weight: 800;
  margin-top: 6px;
}

.saldo-value.saldo-text-green {
  background: linear-gradient(90deg, var(--fg-green-deep), #22c55e);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.saldo-value.saldo-text-amber {
  background: linear-gradient(90deg, var(--fg-amber-deep), #f59e0b);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.saldo-hint {
  font-size: 11.5px;
  color: var(--text-faint);
  margin-top: 4px;
}

/* ===== LINHAS DO BALANÇO ===== */
.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--surface-2);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  padding: 9px 12px;
  margin-top: 8px;
}

.balance-icon {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  flex-shrink: 0;
}

.balance-icon.tone-green {
  background: linear-gradient(135deg, #16a34a, #4ade80);
}

.balance-icon.tone-amber {
  background: linear-gradient(135deg, #d97706, #fbbf24);
}

.balance-icon.tone-slate {
  background: linear-gradient(135deg, #64748b, #94a3b8);
}

.balance-row-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-2);
}

.balance-row-value {
  font-size: 13px;
  font-weight: 800;
}

.balance-row-value.text-green {
  color: var(--fg-green);
}

.balance-row-value.text-amber {
  color: var(--fg-amber-deep);
}

.balance-row-value.text-slate {
  color: var(--text-muted);
}

/* ===== HISTÓRICO ===== */
.custom-table ::v-deep .v-data-table-header th {
  font-weight: 700;
  color: var(--text-3);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: var(--tint-purple);
}

.border-top {
  border-top: 1px solid var(--border-soft);
}

/* ===== ANIMAÇÃO ===== */
.fade-up {
  animation: fadeUp 0.5s ease both;
}

.d1 {
  animation-delay: 0.05s;
}

.d2 {
  animation-delay: 0.1s;
}

.d3 {
  animation-delay: 0.18s;
}

.d4 {
  animation-delay: 0.24s;
}

.d5 {
  animation-delay: 0.3s;
}

.d6 {
  animation-delay: 0.38s;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .fade-up {
    animation: none;
  }
}
</style>
